import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import {
  RotacionPesajeVeterinariaItem,
  RotacionPesajeVeterinariaService,
} from "../../../../generated/grpc/pesaje_ganaderia_pb";
import { grpcRotacionPesajeVeterinariaDatabase } from "../database/GrpcRotacionPesajeVeterinariaDatabase";
import { serviceSync } from "../../../../base/services/ServiceSync";
import { fx_nombreLote, fx_nombresLotesAsync } from "../../../terceros/services/LoteResumenUtils";
import { RotacionPesajeVeterinariaResponse } from "../response/RotacionPesajeVeterinariaResponse";

/** Variante gRPC-Web (con delta real, por finca) de `RotacionPesajeVeterinariaGetListByFincaIdService`
 *  (ver ese archivo). Devuelve `RotacionPesajeVeterinariaResponse[]` -mismo
 *  shape que ya usa el resto de la app-. Rellena `clienteId` (sin uso real). */
class RotacionPesajeVeterinariaGrpcService {
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<RotacionPesajeVeterinariaResponse[]> {
    await this._syncAsync();
    const items =
      await grpcRotacionPesajeVeterinariaDatabase.getListByFincaIdAsync<RotacionPesajeVeterinariaItem>(
        this._fincaId,
      );
    // Nombre actual de cada potrero: el evento guarda el que tenía al sincronizarse.
    const nombresLotes = await fx_nombresLotesAsync(this._fincaId);
    return items.map((item) => toRotacionPesajeVeterinariaResponse(item, nombresLotes));
  }

  clearCache(): void {
    serviceSync.reset(`grpc:rotaciones:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:rotaciones:${this._fincaId}`, async () => {
      const ultimaSincronizacion =
        await grpcRotacionPesajeVeterinariaDatabase.getUltimaSincronizacionAsync(this._fincaId);
      const client = createClient(RotacionPesajeVeterinariaService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcRotacionPesajeVeterinariaDatabase.putDeltaAsync(
        this._fincaId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createRotacionPesajeVeterinariaGetListByFincaIdService`). */
export const fx_createRotacionPesajeVeterinariaGrpcService = createCachedFactory(
  (fincaId: string) => new RotacionPesajeVeterinariaGrpcService(fincaId),
);

/** Adapta un `RotacionPesajeVeterinariaItem` (gRPC) al shape
 *  `RotacionPesajeVeterinariaResponse` (REST). Exportada porque los
 *  servicios de guardado/borrado también la necesitan.
 *  @param nombresLotes Nombres actuales de los lotes (`fx_nombresLotesAsync`);
 *  sin él se usan los que trae el evento. */
export function toRotacionPesajeVeterinariaResponse(
  rotacion: RotacionPesajeVeterinariaItem,
  nombresLotes?: ReadonlyMap<string, string>,
): RotacionPesajeVeterinariaResponse {
  const loteDestinoId = rotacion.loteDestinoId ?? null;
  return {
    id: rotacion.id,
    isEliminado: rotacion.eliminado,
    clienteId: "",
    fincaId: rotacion.fincaId,
    loteOrigenId: rotacion.loteOrigenId,
    loteOrigenNombre: fx_nombreLote(nombresLotes, rotacion.loteOrigenId, rotacion.loteOrigenNombre),
    loteDestinoId,
    loteDestinoNombre:
      loteDestinoId === null
        ? (rotacion.loteDestinoNombre ?? null)
        : fx_nombreLote(nombresLotes, loteDestinoId, rotacion.loteDestinoNombre ?? ""),
    fecha: rotacion.fecha,
    cantidadAnimales: rotacion.cantidadAnimales,
    isRotacion: rotacion.isRotacion,
    isPesaje: rotacion.isPesaje,
    isVeterinaria: rotacion.isVeterinaria,
    pesoTotal: rotacion.pesoTotal,
    pesoPromedio: rotacion.pesoPromedio ?? null,
    pesoMinimo: rotacion.pesoMinimo ?? null,
    pesoMaximo: rotacion.pesoMaximo ?? null,
    pesosAnimales: rotacion.pesosAnimales,
    vetObservaciones: rotacion.vetObservaciones ?? null,
    procedimientoIds: rotacion.procedimientoIds,
  };
}
